import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import TransparentCard from 'component/misc/TransparentCard';
import { formatCurrencyOrUnit, withPerUnit } from 'utils/formatUnit';

const getLocaleFromLang = (lang = 'en') => {
    const lower = String(lang || 'en').toLowerCase();
    if (lower.startsWith('de')) return 'de-DE';
    if (lower.startsWith('zh')) return 'zh-CN';
    return 'en-US';
};

const getLocaleFromNumberFormat = (numberFormat, fallbackLang) => {
    const format = String(numberFormat || 'auto').toLowerCase();
    if (format === 'us') return 'en-US';
    if (format === 'eu') return 'de-DE';
    return getLocaleFromLang(fallbackLang);
};

const getDisplayPrecision = (priceDisplay) => {
    if (!priceDisplay) return 2;
    const str = String(priceDisplay);
    const match = str.match(/[-+]?\d[\d.,]*/);
    if (!match) return 2;
    const numeric = match[0];
    const lastComma = numeric.lastIndexOf(',');
    const lastDot = numeric.lastIndexOf('.');
    const sepIdx = Math.max(lastComma, lastDot);
    if (sepIdx < 0) return 2;
    return Math.max(0, Math.min(6, numeric.length - sepIdx - 1));
};

const formatSigned = (value, decimals, locale) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
    const num = Number(value);
    const sign = num >= 0 ? '+' : '-';
    return `${sign}${Math.abs(num).toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })}`;
};

const buildPriceDisplay = (material, locale) => {
    const value = Number(material?.last_price_value);
    if (material?.last_price_value == null || !Number.isFinite(value)) {
        return material?.last_price_display;
    }
    const formatted = value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return withPerUnit(formatted, material.currency, material.value_unit);
};

const ChartHeader = ({ material, hasComparisons = false, onRemove, color = '#8B5CF6' }) => {
    const { t, i18n } = useTranslation();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const locale = getLocaleFromNumberFormat(material?.number_format, i18n.resolvedLanguage || i18n.language);

    // Use material data from props if available, otherwise use fallback
    const displayData = material ? {
        title: material.name,
        subtitle: material.description,
        // Aus Wert + Waehrung + Einheit aufbauen, damit nie ein roher
        // Einheitencode (z. B. "USD/brl_US" aus /general) im Kopf steht.
        price: buildPriceDisplay(material, locale),
        priceValue: material.last_price_value,
        date: material.last_price_date_display,
        changePercent: material.change_pct_value,
        changeAbsolute: material.change_abs_value,
        // Absolute Aenderung in Waehrung, bei Reihen ohne Waehrung in der Einheit (z. B. GWh)
        changeUnit: formatCurrencyOrUnit(material.currency, material.value_unit)
    } : {
        title: t('charts.header.fallbackTitle'),
        subtitle: t('charts.header.fallbackSubtitle'),
        price: t('charts.header.fallbackPrice'),
        priceValue: null,
        date: t('charts.header.fallbackDate'),
        changePercent: 0.79,
        changeAbsolute: 607.71,
        changeUnit: 'CNY'
    };

    // Format price change
    const decimals = getDisplayPrecision(displayData.price);
    const absChange = Number.isFinite(Number(displayData.changeAbsolute)) ? Number(displayData.changeAbsolute) : null;
    const lastPrice = Number.isFinite(Number(displayData.priceValue)) ? Number(displayData.priceValue) : null;

    let pctChange = Number.isFinite(Number(displayData.changePercent)) ? Number(displayData.changePercent) : null;
    if (pctChange === null && absChange !== null && lastPrice !== null) {
        const previous = lastPrice - absChange;
        if (previous !== 0) {
            pctChange = (absChange / previous) * 100;
        }
    }

    const pctText = formatSigned(pctChange, decimals, locale);
    const absText = formatSigned(absChange, decimals, locale);
    const changeText = pctText && absText
        ? `${pctText}% / ${absText}${displayData.changeUnit ? ` ${displayData.changeUnit}` : ''}`
        : '';

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    return (
        <TransparentCard
            className="glass-card-ui"
            style={hasComparisons ? { borderLeft: `4px solid ${color}` } : {}}
        >
            <div className="d-flex justify-content-between align-items-center flex-wrap">
                <div style={{ flex: 1 }}>
                    <h1 className='chart-title'>{displayData.title}</h1>
                    {!isCollapsed && (
                        <p className='chart-subtitle'>{displayData.subtitle}</p>
                    )}
                </div>
                <div className='text-end d-flex align-items-center'>
                    <div className={hasComparisons ? 'me-3' : ''}>
                        <h1 className='chart-price'>{displayData.price}</h1>
                        {!isCollapsed && (
                            <>
                                <p className='chart-price-date'>{displayData.date}</p>
                                {changeText && <p className='chart-price-change'>{changeText}</p>}
                            </>
                        )}
                    </div>
                    {hasComparisons && (
                        <div className='d-flex flex-column gap-2'>
                            <button
                                onClick={toggleCollapse}
                                className="btn btn-sm collapse-toggle-btn"
                                style={{
                                    background: 'rgba(116, 121, 255, 0.1)',
                                    border: '1px solid rgba(116, 121, 255, 0.3)',
                                    borderRadius: '6px',
                                    color: '#7479ff',
                                    padding: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.3s ease'
                                }}
                                title={isCollapsed ? t('charts.header.expand') : t('charts.header.collapse')}
                            >
                                {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                            </button>
                            <button
                                onClick={onRemove}
                                className="btn btn-sm"
                                style={{
                                    background: 'rgba(239, 68, 68, 0.1)',
                                    border: '1px solid rgba(239, 68, 68, 0.3)',
                                    borderRadius: '6px',
                                    color: '#EF4444',
                                    padding: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                title={t('charts.header.removeMaterial')}
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </TransparentCard>
    );
};

export default ChartHeader;
